/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { supabase } from '../../../shared/supabase/supabaseClient';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import type { ReportsVentasLayoutMode } from '../reportsVentasLayout';
import { styles } from '../ReportsScreen.styles';
import { ReportsScopeEntityFilterPicker } from './ReportsScopeEntityFilterPicker';
import { ReportsScopeFilterSheet, ReportsScopeFilterSheetRow } from './ReportsScopeFilterSheet';
type CustomerRow = { id: string; name: string };

export function ReportsCustomerFilterControl({
  vm,
  disabled,
  layout,
}: {
  vm: ReportsScreenViewModel;
  disabled?: boolean;
  layout: ReportsVentasLayoutMode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerRow[]>([]);

  const active = vm.reportsCustomerFilterId != null;
  const valueLabel = vm.reportsCustomerFilterLabel ?? 'Cliente';

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      void (async () => {
        const q = query.trim();
        let sq = supabase.schema('customers').from('customers').select('id, name').order('name').limit(40);
                    if (q) {
                      sq = sq.ilike('name', `%${q.replace(/[%_]/g, '\\$&')}%`);
                    }
                    const { data, error } = await sq;
                    if (error) {
                      setResults([]);
                      return;
                    }
                    setResults((data ?? []) as CustomerRow[]);
                    return;
      })();
    }, 200);
    return () => clearTimeout(t);
  }, [open, query]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const onSelect = useCallback(
    (row: CustomerRow) => {
      vm.setReportsCustomerFilter(row.id, row.name);
      setOpen(false);
    },
    [vm]
  );

  const onClear = useCallback(() => {
    vm.clearReportsCustomerFilter();
    setOpen(false);
  }, [vm]);

  const searchField = (
    <View style={{ marginBottom: spacing.sm }}>
      <TextInput
        style={styles.input}
        placeholder="Buscar por nombre"
        placeholderTextColor={colors.textMuted}
        value={query}
        onChangeText={setQuery}
        autoCapitalize="words"
        accessibilityLabel="Buscar cliente"
      />
    </View>
  );

  return (
    <>
      <ReportsScopeEntityFilterPicker
        disabled={disabled}
        active={active}
        caption="Cliente"
        valueLabel={valueLabel}
        emptyLabel="Todos"
        onPress={() => setOpen(true)}
        layout={layout}
        accessibilityLabel={
          active ? `Cliente: ${valueLabel}. Cambiar filtro` : 'Filtrar por cliente. Abrir lista'
        }
      />

      <ReportsScopeFilterSheet
        visible={open}
        title="Cliente"
        onClose={() => setOpen(false)}
        hint="Ventas cobradas a ese cliente en el período."
        headerAccessory={searchField}
        maxScrollHeight={360}
      >
        <ReportsScopeFilterSheetRow
          label="Todos los clientes"
          selected={!active}
          onPress={onClear}
          disabled={disabled}
        />
        {results.map((row) => (
          <ReportsScopeFilterSheetRow
            key={row.id}
            label={row.name}
            selected={vm.reportsCustomerFilterId === row.id}
            onPress={() => onSelect(row)}
            disabled={disabled}
          />
        ))}
        {results.length === 0 ? (
          <ReportsScopeFilterSheetRow
            label={query.trim() ? 'Sin clientes que coincidan' : 'Sin clientes cargados'}
            selected={false}
            onPress={() => {}}
            disabled
          />
        ) : null}
      </ReportsScopeFilterSheet>
    </>
  );
}
